import { Cancel, Edit } from '@mui/icons-material';
import {
  Autocomplete,
  Chip,
  DialogActions,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { calculateFundRateCost } from 'lib/functions/fundCostRateCalculation';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface ResellData {
  id?: string;
  rate: string;
  operationCostFixed: number;
  operationCostVariable: number;
  amount: number;
  advance: number;
  arrearRate: number;
  days: number;
  fund: any;
  rateCost: number;
  operationCostRateVariable: string;
  startDate: string;
  endDate: string;
  documentId: string | null;
  observation: string;
  fundId: string | null;
  status?: string;
  ownCollectionAt?: string;
  buybackAt?: string;
  FundPayment?: any[];
}

interface DocumentData {
  id: string;
  folio: string;
  businessName: string;
  stakeholderName: string;
  amount: number;
  amountConciliated: number;
  totalRemaining: number;
  startingPaymentDate: string;
  paymentDate: string;
  documentRemainingAmount: number;
}

interface FundData {
  id: string;
  name: string;
}

import { getCompanyConfig } from '../../../../../../../../../lib/api/company';
import { getDocumentWithExtensionData } from '../../../../../../../../../lib/api/extension/factoring';
import { getCompanyFunds } from '../../../../../../../../../lib/api/funds';
import {
  createFactoringReSell,
  getDocumentsAvailableForReSelling,
  getFactoringReSell,
  replaceResellDocument,
  updateFactoringReSell,
} from '../../../../../../../../../lib/api/reSell/factoring';
import CustomButton from '../../../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../../../lib/designSystem/CircularProgress';
import { LightTooltip } from '../../../../../../../../../lib/designSystem/WhiteTooltip';
import { isDateAWeekDay } from '../../../../../../../../../lib/functions/dates';
import {
  formatDate,
  formatMoney,
  showAlert,
  validatePermissions,
} from '../../../../../../../../../lib/functions/utils';
import { AuthState } from '../../../../../../../../../services/redux/authSlice';
import RevertResell from '../../../RevertResell';
import DocumentEditor from './components/DocumentEditor';

interface DocumentFormParametersProps {
  document: DocumentData;
  resell: ResellData;
  setResell: (resell: ResellData) => void;
  setCanModifyParams: (canModify: boolean) => void;
  canModifiyParams: boolean;
  funds: FundData[];
  canForcePaymentDate: boolean;
}

const DocumentFormParameters = (props: DocumentFormParametersProps) => {
  const {
    document,
    resell,
    setResell,
    setCanModifyParams,
    canModifiyParams,
    canForcePaymentDate,
  } = props;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [funds, setFunds] = useState<FundData[]>([]);
  const [fundsCostDict, setFundsCostDict] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [fundCalculationMethodHash, setFundCalculationMethodHash] =
    useState<any>({});
  const [tempEndDate, setTempEndDate] = useState(resell.endDate);
  const calculateDays = (start: Date, end: Date) => {
    const days = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
    );
    return days;
  };
  const [days, setDays] = useState(
    calculateDays(new Date(resell.startDate), new Date(resell.endDate)),
  );

  const dateValidators = (date: Date, type: 'startDate' | 'endDate') => {
    if (canForcePaymentDate) {
      const startDate =
        type === 'startDate' ? date : new Date(resell.startDate);
      const endDate = type === 'endDate' ? date : new Date(resell.endDate);
      const days = calculateDays(startDate, endDate);
      if (days <= 0) {
        alert(
          'La fecha de vencimiento no puede ser menor a la fecha de liberación',
        );
        return false;
      }
      return days;
    }
    if (!isDateAWeekDay(date) && type === 'endDate') {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento seleccionada no es un día hábil.',
      );
    }
    if (date < new Date() && type === 'endDate') {
      alert('La fecha de vencimiento no puede ser menor a la fecha actual');
      setTempEndDate(resell.endDate);
      return false;
    }
    const startDate = type === 'startDate' ? date : new Date(resell.startDate);
    const endDate = type === 'endDate' ? date : new Date(resell.endDate);
    const days = calculateDays(startDate, endDate);
    if (days <= 0) {
      alert(
        'La fecha de vencimiento no puede ser menor a la fecha de liberación',
      );
      return false;
    }
    return days;
  };

  const handleChangeDate = (value: string, type: 'startDate' | 'endDate') => {
    const days = dateValidators(new Date(value), type);
    if (!days) return;
    setDays(days);

    const rateCost = calculateFundRateCost(
      resell.amount,
      resell.advance,
      resell.operationCostFixed,
      parseFloat(resell.rate),
      days,
      fundCalculationMethodHash,
      resell.fund?.id,
    );
    setResell({
      ...resell,
      [type]: value,
      rateCost,
    });
  };

  const handleChangeFund = (
    e: React.SyntheticEvent,
    value: FundData | null,
  ) => {
    const rate = value ? fundsCostDict[value.id] : 0;
    const rateCost = calculateFundRateCost(
      resell.amount,
      resell.advance,
      resell.operationCostFixed,
      rate,
      days,
      fundCalculationMethodHash,
      value?.id,
    );
    setResell({
      ...resell,
      fundId: value ? value.id : null,
      fund: value ? value : null,
      rateCost,
      rate,
    });
  };

  const handleChangeAmount = (e: React.SyntheticEvent) => {
    const value = (e.target as HTMLInputElement).value
      .split('.')
      .join('')
      .split('$')
      .join('');
    if (isNaN(parseFloat(value))) return;
    if (parseFloat(value) < 0) return;
    if (parseFloat(value) > document.amount)
      return alert(
        'El monto a financiar no puede ser mayor al monto del documento',
      );
    const rateCost = calculateFundRateCost(
      parseFloat(value),
      resell.advance,
      resell.operationCostFixed,
      parseFloat(resell.rate),
      days,
      fundCalculationMethodHash,
      resell.fund?.id,
    );
    setResell({
      ...resell,
      amount: parseFloat(value),
      rateCost,
    });
  };

  const getRate = (value: string) => {
    if (value === '') {
      return 0;
    }
    if (value.endsWith('.')) {
      return parseFloat(value);
    }
    return parseFloat(value);
  };

  const handleChangeRate = (e: any, type: string) => {
    let valueToCalculate = 0;
    let value = e.target.value;
    if (type === 'amount') {
      value = value.split('.').join('').split('$').join('');
      if (value === '') {
        valueToCalculate = 0;
      } else if (isNaN(parseFloat(value))) {
        return;
      } else {
        valueToCalculate = parseFloat(value);
      }
    } else {
      const isValidFormat = /^\d*\.?\d*$/.test(value);
      if (!isValidFormat) return;
      valueToCalculate = getRate(value);
      if (valueToCalculate > 100 || valueToCalculate < 0) return;
    }
    const rateCost = calculateFundRateCost(
      type === 'amount' ? valueToCalculate : resell.amount,
      type === 'advance' ? valueToCalculate : resell.advance,
      resell.operationCostFixed,
      type === 'rate' ? valueToCalculate : parseFloat(resell.rate),
      days,
      fundCalculationMethodHash,
      resell.fund?.id,
    );
    setResell({
      ...resell,
      [type]: value,
      rateCost: rateCost,
    });
  };

  const handleChangeOperationCostFixed = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value === '') {
      setResell({
        ...resell,
        operationCostFixed: 0,
      });
      return;
    }
    const numericValue = parseInt(value, 10);
    if (isNaN(numericValue) || numericValue < 0) return;
    setResell({
      ...resell,
      operationCostFixed: parseFloat(value),
    });
  };

  const canEditByStatus =
    resell.status === 'En revisión' || resell.status === 'Activa';

  const canEditByUser = validatePermissions(user, 'financing_fundSale_edit');

  const fetchFunds = async () => {
    setLoading(true);
    try {
      const funds = await getCompanyFunds(dispatch);
      const fundsCostDict = funds.reduce((acc: any, fund: any) => {
        acc[fund.id] = fund.cost;
        return acc;
      }, {});
      const fundCalculationMethodHash = funds.reduce(
        (acc: Record<string, string | undefined>, fund: any) => {
          const calculationMethod =
            fund.FundCompanyRelation?.[0]?.calculationMethod || 'SIMPLE';

          if (fund.id) {
            acc[fund.id] = calculationMethod;
          }
          return acc;
        },
        {} as Record<string, string | undefined>,
      );
      setFundsCostDict(fundsCostDict);
      setFundCalculationMethodHash(fundCalculationMethodHash);
      setFunds(funds);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchFunds();
  }, []);

  if (loading) {
    return <CenteredCircularProgress type="dialog" />;
  }

  return (
    <div style={{ margin: '8px 0px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          margin: '16px 0px 0px',
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Datos de la venta
        </Typography>
        {canEditByStatus && canEditByUser ? (
          <div>
            <IconButton
              onClick={() => setCanModifyParams(!canModifiyParams)}
              style={{ marginLeft: 'auto' }}
            >
              {canModifiyParams ? <Cancel /> : <Edit />}
            </IconButton>
          </div>
        ) : null}
      </div>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6}>
          <Typography variant="body1" fontWeight="bold" margin={'8px 0px'}>
            Parámetros
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha de inicio"
                type="date"
                value={resell.startDate}
                disabled={!canModifiyParams}
                onChange={(e) => {
                  handleChangeDate(e.target.value, 'startDate');
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fecha de vencimiento"
                type="date"
                value={tempEndDate}
                disabled={!canModifiyParams}
                onChange={(e) => {
                  setTempEndDate(e.target.value);
                }}
                onBlur={() => handleChangeDate(tempEndDate, 'endDate')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monto a financiar"
                value={formatMoney(resell.amount)}
                onChange={(e) => handleChangeAmount(e)}
                disabled={!canModifiyParams}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Anticipo"
                    value={resell.advance}
                    onChange={(e) => handleChangeRate(e, 'advance')}
                    disabled={!canModifiyParams}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Tasa Mora"
                    value={resell.arrearRate}
                    onChange={(e) => handleChangeRate(e, 'arrearRate')}
                    disabled={!canModifiyParams}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Tasa"
                    value={resell.rate}
                    onChange={(e) => handleChangeRate(e, 'rate')}
                    disabled={!canModifiyParams}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Comisión"
                    value={formatMoney(resell.operationCostFixed)}
                    onChange={handleChangeOperationCostFixed}
                    disabled={!canModifiyParams}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={funds}
                value={funds.find((x: any) => x.id === resell.fundId)}
                getOptionLabel={(option: any) =>
                  `${option.name} | ${option.id}`
                }
                renderInput={(params: any) => (
                  <TextField {...params} label="Selecciona un fondo" />
                )}
                onChange={handleChangeFund}
                fullWidth
                disabled={!canModifiyParams}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div style={{ borderLeft: '0.5px solid #000', paddingLeft: 16 }}>
            <Typography variant="body1" fontWeight="bold" margin={'8px 0px'}>
              Resumen
            </Typography>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight="bold" margin={'8px 0px'}>
                Monto a financiar
              </Typography>
              <Typography variant="body2">
                {formatMoney(resell.amount)}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight="bold" margin={'8px 0px'}>
                Monto a anticipado ({resell.advance}%)
              </Typography>
              <Typography variant="body2">
                {formatMoney(
                  Math.round((resell.advance * resell.amount) / 100),
                )}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight="bold" margin={'8px 0px'}>
                Costo tasa ({days} Días)
              </Typography>
              <Typography variant="body2">
                {formatMoney(resell.rateCost)}
              </Typography>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight="bold" margin={'8px 0px'}>
                Costo operación + IVA
              </Typography>
              <Typography variant="body2">
                {formatMoney(
                  (Number(resell.operationCostFixed || 0) +
                    Number(resell.operationCostVariable || 0)) *
                    1.19,
                )}
              </Typography>
            </div>
            <Divider />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight="bold" margin={'8px 0px'}>
                Total
              </Typography>
              <Typography variant="body2">
                {formatMoney(
                  ((resell.operationCostFixed || 0) +
                    (resell.operationCostVariable || 0)) *
                    1.19 +
                    resell.rateCost,
                )}
              </Typography>
            </div>
          </div>
        </Grid>
      </Grid>
      <div>
        <Typography variant="body1" fontWeight="bold" margin={'8px 0px'}>
          Observaciones
        </Typography>
        <TextField
          fullWidth
          disabled={!canModifiyParams}
          multiline
          rows={4}
          value={resell.observation}
          onChange={(e) =>
            setResell({ ...resell, observation: e.target.value })
          }
        />
      </div>
    </div>
  );
};

interface DocumentPreviewDataProps {
  document: DocumentData;
  setSearched: (searched: boolean) => void;
  edit: boolean;
  resell: ResellData;
  setOpenDocumentEditor: (open: boolean) => void;
}

const DocumentPreviewData = (props: DocumentPreviewDataProps) => {
  const { document, setSearched, edit, resell, setOpenDocumentEditor } = props;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const canEditByStatus =
    resell.status === 'En revisión' || resell.status === 'Activa';
  const canEditByUser = validatePermissions(user, 'financing_fundSale_edit');
  const chipsConfig = [
    {
      key: 'ownCollectionAt',
      show: !!resell?.ownCollectionAt,
      label: 'Recaudación propia',
      color: 'primary' as const,
      tooltip: resell?.ownCollectionAt
        ? formatDate(new Date(resell.ownCollectionAt))
        : '',
    },
    {
      key: 'buybackAt',
      show: !!resell?.buybackAt,
      label: 'Recomprada',
      color: 'success' as const,
      tooltip: resell?.buybackAt ? formatDate(new Date(resell.buybackAt)) : '',
    },
  ];
  return (
    <div style={{ margin: '0px 0px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          margin: '16px 0px 0px',
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Datos del documento
        </Typography>
        {canEditByStatus && canEditByUser ? (
          <div>
            <IconButton
              onClick={() => setOpenDocumentEditor(true)}
              style={{ marginLeft: 'auto' }}
            >
              <Edit />
            </IconButton>
          </div>
        ) : null}
        {!edit && (
          <Typography
            variant="caption"
            fontWeight="bold"
            padding={'8px 8px'}
            style={{ cursor: 'pointer', color: 'blue', marginLeft: 'auto' }}
            onClick={() => setSearched(false)}
          >
            Cambiar
          </Typography>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          margin: '8px 0 8px 0',
        }}
      >
        {chipsConfig
          .filter((chip) => chip.show)
          .map((chip) => (
            <LightTooltip
              key={chip.key}
              title={
                <div style={{ padding: '10px' }}>
                  <Typography variant="body2">{chip.tooltip}</Typography>
                </div>
              }
              placement="bottom"
            >
              <Chip
                size="small"
                label={chip.label}
                color={chip.color}
                variant="outlined"
                sx={{
                  fontWeight: 'bold',
                  minWidth: 110,
                }}
              />
            </LightTooltip>
          ))}
      </div>
      <div style={{ display: 'flex', width: '100%' }}>
        <Grid container spacing={2} style={{ flex: 1 }}>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Cliente</Typography>
            <Typography variant="body2">{document.businessName}</Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Deudor</Typography>
            <Typography variant="body2">{document.stakeholderName}</Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Liberación Original</Typography>
            <Typography variant="body2">
              {formatDate(new Date(document.startingPaymentDate))}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Liberación actual</Typography>
            <Typography variant="body2">
              {formatDate(new Date(document.paymentDate))}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Folio</Typography>
            <Typography variant="body2">{document.folio}</Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Monto documento</Typography>
            <Typography variant="body2">
              {formatMoney(document.amount)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Abonos</Typography>
            <Typography variant="body2">
              {formatMoney(document.amountConciliated)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="body2">Saldo deuda</Typography>
            <Typography variant="body2">
              {' '}
              {formatMoney(document.totalRemaining)}
            </Typography>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

interface ResellMainProps {
  resell: ResellData;
  setResell: (resell: ResellData) => void;
  setOpen: (open: boolean) => void;
  setUp: () => Promise<void>;
  edit: boolean;
  triggerSearch: () => Promise<void>;
}

const ResellMain = (props: ResellMainProps) => {
  const { resell, setResell, setOpen, setUp, edit, triggerSearch } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<DocumentData[]>([]);
  const [document, setDocument] = useState<DocumentData | null>(null);
  const [searched, setSearched] = useState(false);
  const [funds, setFunds] = useState<FundData[]>([]);
  const [canModifyParams, setCanModifyParams] = useState(!edit);
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [canForcePaymentDate, setCanForcePaymentDate] = useState(false);
  const [resellToRevert, setResellToRevert] = useState<string | null>(null);
  const [openRevert, setOpenRevert] = useState(false);
  const [openDocumentEditor, setOpenDocumentEditor] = useState(false);

  const canEditByStatus =
    resell.status === 'En revisión' || resell.status === 'Activa';
  const canEditByUser = validatePermissions(user, 'financing_fundSale_edit');

  const validParams = () => {
    return true;
  };

  const handleChangeDocument = (
    event: React.SyntheticEvent,
    newValue: DocumentData | null,
  ) => {
    setDocument(newValue);
    setResell({ ...resell, documentId: newValue?.id || null });
  };

  const handleSearchData = async () => {
    setLoading(true);
    try {
      if (!document) return;
      const response = await getDocumentWithExtensionData(
        dispatch,
        document.id,
      );
      setDocument(response);
      const fundsResponse = await getCompanyFunds(dispatch);
      setFunds(fundsResponse);
      setSearched(true);
    } catch (error) {
      // Error handling
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!validParams()) {
      return;
    }
    setLoading(true);
    try {
      if (edit) {
        if (!resell.id) return;
        await updateFactoringReSell(dispatch, resell.id, {
          rate: parseFloat(resell.rate),
          rateCost: Number(resell.rateCost),
          amount: Number(resell.amount),
          advance: Number(resell.advance),
          arrearRate: Number(resell.arrearRate),
          startDate: new Date(resell.startDate),
          dueDate: new Date(resell.endDate),
          operationCostRateVariable: parseFloat(
            resell.operationCostRateVariable || '0',
          ),
          operationCostFixed: resell.operationCostFixed || 0,
          observation: resell.observation,
          fundId: resell.fundId,
        });
        setOpen(false);
        showAlert(dispatch, 'success', 'Prórroga actualizada correctamente');
        try {
          await setUp();
        } catch (refreshError) {
          // Error al refrescar la lista, pero la actualización fue exitosa
        }
        return;
      } else {
        await createFactoringReSell(dispatch, {
          ...resell,
          rate: parseFloat(resell.rate),
          rateCost: Number(resell.rateCost),
          endDate: new Date(resell.endDate),
          startDate: new Date(resell.startDate),
          operationCostRateVariable: parseFloat(
            resell.operationCostRateVariable || '0',
          ),
          operationCostFixed: resell.operationCostFixed,
          fundId: resell.fundId,
          observation: resell.observation,
        });
      }
      setOpen(false);
      showAlert(dispatch, 'success', 'Venta creada correctamente');
      try {
        await setUp();
      } catch (refreshError) {
        // Error al refrescar la lista, pero la creación fue exitosa
      }
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al crear la venta');
    }
    setLoading(false);
  };

  const setUpSuggestions = async () => {
    setLoading(true);
    try {
      const response = await getDocumentsAvailableForReSelling(dispatch);
      setSuggestions(response.documentsProcessed);
    } catch (error) {
      // Error handling
    }
    setLoading(false);
  };

  const setUpConfig = async () => {
    try {
      const response = await getCompanyConfig(
        dispatch,
        'system_operations_fund_forcePaymentDate',
      );
      if (response === '1') {
        setCanForcePaymentDate(true);
      }
    } catch (error) {
      console.error('Error fetching date restrictions config', error);
    }
  };

  const setUpResell = async () => {
    if (edit) {
      setLoading(true);
      try {
        if (!resell.id) return;
        const response = await getFactoringReSell(dispatch, resell.id);
        setResell({
          ...response.reSell,
          startDate: formatDate(response.reSell.startDate),
          endDate: formatDate(response.reSell.dueDate),
          operationCostFixed: response.reSell.operationCostFixed || 0,
        });
        const fundsResponse = await getCompanyFunds(dispatch);
        setFunds(fundsResponse);
        setDocument(response.document);
        setSearched(true);
      } catch (error) {
        // Error handling
      }
      setLoading(false);
    } else {
      await setUpSuggestions();
    }
  };

  const setUpDialog = async () => {
    await setUpResell();
    await setUpConfig();
  };

  const handleRevert = () => {
    if (resell.FundPayment && resell.FundPayment.length > 0) {
      showAlert(
        dispatch,
        'warning',
        'Debe eliminar los pagos antes de revertir',
      );
      return;
    }
    setResellToRevert(resell.id || null);
    setOpenRevert(true);
  };

  const handleDocumentReplace = async (newDocumentId: string) => {
    try {
      await replaceResellDocument(dispatch, resell.id || '', {
        newDocumentId,
      });

      const updatedResell = {
        ...resell,
        documentId: newDocumentId,
      };
      setResell(updatedResell);

      const response = await getDocumentWithExtensionData(
        dispatch,
        newDocumentId,
      );
      setDocument(response);

      showAlert(dispatch, 'success', 'Documento reemplazado correctamente');
    } catch (error) {
      console.error('Error replacing document:', error);
      showAlert(dispatch, 'error', 'Error al reemplazar el documento');
      throw error;
    }
  };

  useEffect(() => {
    void setUpDialog();
  }, []);

  return (
    <div>
      {loading ? (
        <CenteredCircularProgress type="dialog" />
      ) : (
        <div>
          {!searched && !edit && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Autocomplete
                style={{ margin: '8px 0px' }}
                options={suggestions}
                getOptionLabel={(option: any) =>
                  `${option.folio} | ${option.businessName} | ${option.stakeholderName}`
                }
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    label="Busca por folio, cliente o deudor"
                  />
                )}
                onChange={handleChangeDocument}
                fullWidth
              />
              <div style={{ padding: '0px 16px' }}>
                <CustomButton
                  onClick={() => handleSearchData()}
                  color="primary"
                >
                  Seleccionar
                </CustomButton>
              </div>
            </div>
          )}
          {searched && (
            <div>
              {document && (
                <>
                  <DocumentPreviewData
                    document={document}
                    setSearched={setSearched}
                    edit={edit}
                    resell={resell}
                    setOpenDocumentEditor={setOpenDocumentEditor}
                  />
                  <DocumentFormParameters
                    document={document}
                    resell={resell}
                    setResell={setResell}
                    canModifiyParams={canModifyParams}
                    setCanModifyParams={setCanModifyParams}
                    funds={funds}
                    canForcePaymentDate={canForcePaymentDate}
                  />
                </>
              )}
            </div>
          )}

          <DialogActions>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0px',
                gap: '10px',
              }}
            >
              <Typography
                color="error"
                variant="body1"
                fontWeight="bold"
                style={{ cursor: 'pointer' }}
                onClick={() => handleRevert()}
              >
                Eliminar
              </Typography>
              <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
                <CustomButton onClick={() => setOpen(false)} color="secondary">
                  Salir
                </CustomButton>
                {validatePermissions(user, 'financing_fundSale_edit') &&
                  edit &&
                  resell.status === 'En revisión' && (
                    <CustomButton
                      onClick={() => setCanModifyParams(!canModifyParams)}
                      color="warning"
                    >
                      {canModifyParams ? 'Cancelar' : 'Editar'}
                    </CustomButton>
                  )}
                {!edit ||
                resell.status === 'En revisión' ||
                (validatePermissions(user, 'financing_fundSale_edit') &&
                  canModifyParams) ? (
                  <CustomButton
                    onClick={() => handleSubmit()}
                    color="primary"
                    disabled={!canModifyParams}
                  >
                    Guardar
                  </CustomButton>
                ) : null}
              </div>
            </div>
          </DialogActions>
        </div>
      )}
      {openRevert && (
        <RevertResell
          open={openRevert}
          setOpen={setOpenRevert}
          resellToRevert={resellToRevert}
          triggerSearch={triggerSearch}
        />
      )}
      {openDocumentEditor && (
        <DocumentEditor
          open={openDocumentEditor}
          setOpen={setOpenDocumentEditor}
          currentDocument={null}
          onDocumentReplace={handleDocumentReplace}
          canReplace={canEditByStatus && canEditByUser}
          resellId={resell.id || ''}
        />
      )}
    </div>
  );
};

export default ResellMain;
